"use client";

import { useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ArrowLeft } from "lucide-react";

import { InteractiveDemo } from "@/components/interactive-demo";
import { resolveDemoUrlState } from "@/components/interactive-demo/lib/resolve-demo-url-state";
import type { FeatureTab } from "@/components/interactive-demo/types";
import { APP_PAGES_COPY } from "@/app/pages.copy";
import { getLocale } from "@tami/core/intl";
import { localizedPath } from "@/lib/i18n/locale-path";
import type { OrgType } from "@/lib/auth/resolve-org-type";

export function DemoPageClient() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const initial = resolveDemoUrlState({
    tela: searchParams.get("tela"),
    conta: searchParams.get("conta"),
  });

  /*
   * Parâmetro que não existe cai no padrão, e aí a URL passa a dizer uma coisa
   * enquanto a tela mostra outra — `?tela=creators&conta=creator` abre o
   * painel. Normalizar uma vez resolve, e o `useRef` garante o "uma vez": a
   * própria substituição muda os parâmetros que este efeito leria.
   */
  const normalized = useRef(false);
  useEffect(() => {
    if (normalized.current) return;
    normalized.current = true;

    const tela = searchParams.get("tela");
    const conta = searchParams.get("conta");
    if (tela === initial.tab && conta === initial.orgMode) return;

    const params = new URLSearchParams({ tela: initial.tab, conta: initial.orgMode });
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [initial.tab, initial.orgMode, pathname, router, searchParams]);

  /*
   * Troca da pessoa entra no histórico; troca do tour substitui. Sem essa
   * distinção o tour empilha uma entrada por tela e o botão voltar do
   * navegador vira um passeio pela demo inteira.
   */
  const handleStateChange = useCallback(
    ({
      tab,
      orgMode,
      byUser,
    }: {
      tab: FeatureTab;
      orgMode: OrgType;
      byUser: boolean;
    }) => {
      const params = new URLSearchParams({ tela: tab, conta: orgMode });
      const url = `${pathname}?${params.toString()}`;
      if (byUser) {
        router.push(url, { scroll: false });
      } else {
        router.replace(url, { scroll: false });
      }
    },
    [pathname, router],
  );

  return (
    <InteractiveDemo
      /*
       * Sem `key`: remontar a cada troca de tela zeraria o modo e mataria o
       * tour no meio (o tour troca de tela ao fim de cada roteiro). Quem
       * acompanha a URL vinda de fora — link colado, botão voltar — é o efeito
       * de sincronia dentro do próprio componente.
       */
      variant="page"
      /*
       * O voltar e a chamada de cadastro entram na linha de cima da própria
       * demo: uma barra separada custava 60px de altura, e a tela precisa
       * caber com o aparelho e as abas juntos.
       */
      chromeStart={
        <Link
          href={localizedPath("/", getLocale())}
          prefetch={false}
          aria-label={APP_PAGES_COPY.demo.backAria}
          className="flex items-center gap-2 text-[13px] font-medium text-tami-light/60 transition-colors hover:text-tami-light"
        >
          <ArrowLeft className="size-4" aria-hidden />
          <span className="hidden sm:inline">{APP_PAGES_COPY.demo.back}</span>
        </Link>
      }
      chromeEnd={
        <Link
          href="/sign-up"
          prefetch={false}
          data-testid="sign-up-link"
          className="rounded-full bg-tami-green px-4 py-2 text-[13px] font-semibold text-tami-dark transition-colors hover:bg-tami-green-hover"
        >
          {APP_PAGES_COPY.demo.signUp}
        </Link>
      }
      initialTab={initial.tab}
      initialOrgMode={initial.orgMode}
      onStateChange={handleStateChange}
    />
  );
}
